import { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import NotificationPanel from './NotificationPanel';
import SubscriptionWarning from './SubscriptionWarning';
import Chatbot from './Chatbot';
import { productsAPI, authAPI } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useSubscription } from '../hooks/useSubscription';

export default function Layout() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isOpen: sidebarOpen } = useSidebar();
  const { subscription, loading, blocked, warning } = useSubscription();
  
  // Exceção para adm@revicont.dev - sempre permitir acesso
  const isMasterAdmin = user?.email === 'adm@revicont.dev' || (user?.role === 'admin' && !user?.companyId);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [userProfile, setUserProfile] = useState(null);

  // Redirecionar se bloqueado ou se precisa assinar
  useEffect(() => {
    // Exceção para adm@revicont.dev ou admin sem companyId - não redirecionar
    const isMasterAdmin = user?.email === 'adm@revicont.dev' || (user?.role === 'admin' && !user?.companyId);
    if (isMasterAdmin) {
      return;
    }

    // Permitir acesso a /billing e /subscription-blocked mesmo quando bloqueado
    const allowedPages = ['/billing', '/subscription-blocked', '/login', '/register'];
    
    if (blocked && !allowedPages.includes(window.location.pathname)) {
      // Se bloqueado e não está em página permitida, redirecionar para billing
      navigate('/billing');
    } else if (!loading && !subscription && !allowedPages.includes(window.location.pathname)) {
      // Se não tem assinatura e não está em página permitida, redirecionar para billing
      navigate('/billing');
    }
  }, [blocked, loading, subscription, navigate, user]);

  // Função para contar notificações não lidas
  const countUnreadNotifications = async () => {
    try {
      const products = await productsAPI.getAll();
      
      // Verificar se products é um array válido
      if (!products || !Array.isArray(products)) {
        return 0;
      }
      
      // Contar produtos com estoque baixo ou zero
      let totalNotifications = [];
      
      products.forEach(product => {
        if (product && product.stock <= product.minStock && product.stock > 0) {
          totalNotifications.push(`low-stock-${product.id}`);
        }
        if (product && product.stock === 0) {
          totalNotifications.push(`no-stock-${product.id}`);
        }
      });
      
      // Aplicar filtros de lidas e deletadas do localStorage
      try {
        const saved = localStorage.getItem('notifications_state');
        if (saved) {
          const { read = [], deleted = [] } = JSON.parse(saved);
          
          // Filtrar deletadas
          totalNotifications = totalNotifications.filter(id => !deleted.includes(id));
          
          // Filtrar lidas
          totalNotifications = totalNotifications.filter(id => !read.includes(id));
        }
      } catch {}
      
      return totalNotifications.length;
    } catch (error) {
      console.error('Erro ao verificar notificações:', error);
      return 0;
    }
  };

  // Buscar perfil do usuário
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profile = await authAPI.me();
        setUserProfile(profile);
      } catch (error) {
        console.error('Erro ao buscar perfil:', error);
      }
    };
    
    fetchProfile();

    // Listener para atualizar quando o perfil for alterado
    const handleProfileUpdate = () => {
      fetchProfile();
    };

    window.addEventListener('profileUpdated', handleProfileUpdate);

    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
  }, []);

  // Buscar notificações não lidas
  useEffect(() => {
    const checkNotifications = async () => {
      const count = await countUnreadNotifications();
      setUnreadCount(count);
    };

    // Verificar ao carregar
    checkNotifications();

    // Verificar a cada 30 segundos
    const interval = setInterval(checkNotifications, 30000);

    // Listener para atualizar quando localStorage mudar
    const handleStorageChange = () => {
      checkNotifications();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('notificationsUpdated', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('notificationsUpdated', handleStorageChange);
    };
  }, []);

  // Função para obter inicial do nome
  const getInitial = () => {
    if (userProfile?.name) {
      return userProfile.name.charAt(0).toUpperCase();
    }
    if (userProfile?.email) {
      return userProfile.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
      {/* Sidebar */}
      <Sidebar />
      
      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'md:ml-64' : 'md:ml-20'}`}>
        {/* Topbar */}
        <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                EstoqueProAI
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Controle total do seu estoque e finanças
              </p>
            </div>
            
            {/* User Menu */}
            <div className="flex items-center gap-4">
              <button 
                onClick={() => {
                  setShowNotifications(true);
                  // Ao abrir, resetar contador temporariamente
                  // O painel vai atualizar quando abrir
                }}
                className="relative p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors group"
                title={unreadCount > 0 ? `${unreadCount} notificação${unreadCount > 1 ? 'ões' : ''} não lida${unreadCount > 1 ? 's' : ''}` : 'Notificações'}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                
                {/* Ponto vermelho - só aparece se houver notificações */}
                {unreadCount > 0 && (
                  <>
                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                    
                    {/* Badge com número - aparece se tiver mais de 1 */}
                    {unreadCount > 1 && (
                      <span className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1.5 shadow-lg">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </>
                )}
              </button>
              
              <div className="flex items-center gap-3 pl-4 border-l border-gray-200 dark:border-gray-700">
                {/* Nome e empresa */}
                <div className="hidden md:block text-right">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {userProfile?.name || userProfile?.email || 'Usuário'}
                  </p>
                  {userProfile?.company && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {userProfile.company.name}
                    </p>
                  )}
                </div>

                <div className="group relative">
                  <button
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 shadow-xl ring-4 ring-white dark:ring-gray-800 hover:shadow-2xl transition-all cursor-pointer p-1"
                    title={`${userProfile?.name || 'Perfil do usuário'}`}
                  >
                    <div className="w-full h-full rounded-full bg-white dark:bg-gray-800 overflow-hidden flex items-center justify-center">
                      {userProfile?.avatar ? (
                        <img 
                          src={userProfile.avatar} 
                          alt={userProfile.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-blue-600 dark:text-blue-400 font-bold text-sm">{getInitial()}</span>
                      )}
                    </div>
                  </button>
                  
                  {/* Dropdown Menu */}
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    {/* Cabeçalho do dropdown */}
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {userProfile?.name || 'Usuário'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {userProfile?.email}
                      </p>
                      {userProfile?.role === 'admin' && (
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full">
                          Admin Master
                        </span>
                      )}
                    </div>

                    <div className="py-2">
                      <button
                        onClick={() => navigate('/settings')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                      >
                        <span>⚙️</span>
                        <span>Configurações</span>
                      </button>
                      
                      {(userProfile?.email === 'adm@revicont.dev' || (userProfile?.role === 'admin' && !userProfile?.companyId)) && (
                        <button
                          onClick={() => navigate('/super-admin')}
                          className="w-full px-4 py-2 text-left text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors flex items-center gap-2 font-semibold"
                        >
                          <span>👑</span>
                          <span>Painel Super Admin</span>
                        </button>
                      )}

                      <div className="my-1 border-t border-gray-200 dark:border-gray-700"></div>

                      <button
                        onClick={() => {
                          if (confirm('Deseja sair do sistema?')) {
                            authAPI.logout();
                            navigate('/login');
                          }
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-2"
                      >
                        <span>🚪</span>
                        <span>Sair</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          {/* Aviso de assinatura */}
          {warning && (
            <SubscriptionWarning 
              warning={warning}
              subscription={subscription}
            />
          )}
          
          <Outlet />
        </main>
      </div>

      {/* Painel de Notificações */}
      <NotificationPanel 
        isOpen={showNotifications} 
        onClose={async () => {
          setShowNotifications(false);
          // Recarregar contador imediatamente após fechar
          const count = await countUnreadNotifications();
          setUnreadCount(count);
        }} 
      />

      {/* Chatbot */}
      <Chatbot />
    </div>
  );
}

